import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined"
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined"
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined"
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import IconButton from "@mui/material/IconButton"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import Skeleton from "@mui/material/Skeleton"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useSearchParams } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import CloseableSnackbar, {
  useCloseableSnackbarState,
} from "../../../common/components/CloseableSnackbar"
import Column from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import { RowButColumnOnMobile } from "../../../common/components/Row"
import NoResultsTableRow from "../../../common/components/tables/NoResultsTableRow"
import ScrollableTableContainer from "../../../common/components/tables/ScrollableTableContainer"
import TablePaginationRow from "../../../common/components/tables/TablePaginationRow"
import {
  StickyHeaderTableCell,
  StickyTableCell,
} from "../../../common/components/tables/cells"
import {
  getTableSearchParams,
  usePaginationState,
  useTableSort,
} from "../../../common/components/tables/utils"
import FiltersDialog from "../../../smartsetter-utils/filters-dialog"
import { REALITY_FIELD_NAMES } from "../../../smartsetter-utils/filters-dialog/data"
import { useDialogState } from "../../../smartsetter-utils/filters-dialog/utils"
import { canUsePowerDialer, isConcierge } from "../../users/accountRole"
import { isActiveSubscriptionStatus } from "../../users/billing"
import {
  selectProfile,
  selectProfileLoaded,
  selectSubscription,
} from "../../users/reducer"
import AssignToPodButton from "../calling/AssignToPodButton"
import StartPowerDialerButton from "../calling/StartPowerDialerButton"
import { POWER_DIALER_SOURCES } from "../calling/powerDialer"
import ApplyFiltersButton from "../components/ApplyFiltersButton"
import {
  LeadContactActionDialogs,
  useLeadContactActions,
} from "../components/LeadContactActions"
import NameWithContactActionsCell, {
  leadPhoneValue,
  stickyCheckboxCellSx,
  stickyNameCellSx,
} from "../components/NameWithContactActionsCell"
import SavedFilterTabs, {
  SAVED_FILTER_PAGES,
  useSavedFilterTabsState,
} from "../components/SavedFilterTabs"
import SectionSeparator from "../components/SectionSeparator"
import SelectMultipleTableCell, {
  useSelectMultipleTableCellState,
} from "../components/SelectMultipleTableCell"
import {
  DNC_SELECT_TOOLTIP,
  allSelectableChecked,
  dncRowSx,
  isDncLead,
  toggleSelectableRows,
} from "../components/dncLeads"
import {
  PotentialFilterBody,
  RoleFilterBody,
  SchemaBody,
  filterFieldsFor,
} from "../components/leadFilterFields"
import { retrieveNotifications } from "../reducer"
import ConfirmAddLeadsToCandidatesDialog from "./ConfirmAddLeadsToCandidatesDialog"
import ConfirmDeleteUploadedLeadsDialog from "./ConfirmDeleteUploadedLeadsDialog"
import ImportHistoryDrawer from "./ImportHistoryDrawer"
import ImportUploadedLeadsDialog from "./ImportUploadedLeadsDialog"
import {
  LEAD_LIST_TABLE_COLUMN_COUNT,
  LeadListTableDataCells,
  LeadListTableHeaderCells,
  LeadListTableLoadingCells,
} from "./LeadListTableColumns"
import UploadDncDialog from "./UploadDncDialog"
import { mergeLeadFilterConfig } from "./conciergeCallFilterConfig"
import { normalizeLeadCallActivityFilters } from "./leadCallActivityFilters"
import {
  buildLeadsListContext,
  navigateToLeadDetails,
} from "./leadDetailsNavigation"
import { leadListOrdering, shouldIgnoreLeadRowClick } from "./leadList"
import { createLeadRatingChangeHandler } from "./leadRating"
import { serializeUploadedLeadListFilters } from "./uploadedLeadListFilters"
import { useUploadedLeadImportJobPolling } from "./useUploadedLeadImportJobPolling"

export default function UploadedLeadsTab({ onHeaderActionsChange }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [leads, setLeads] = useState([])
  const { checkedIDS, setCheckedIDS, checkedIDsArray, clearSelection } =
    useSelectMultipleTableCellState()
  const [isLoading, setIsLoading] = useState(false)
  const {
    sortColumn,
    sortDirection,
    serverSort,
    onSortColumnChanged,
    setSort,
  } = useTableSort("total_production", "desc", [
    "total_production",
    "total_transactions_count",
  ])
  const {
    pageIndex,
    setPageIndex,
    totalResultsSize,
    setTotalResultsSize,
    pageSize,
    setPageSize,
  } = usePaginationState()
  const {
    filters,
    setFilters,
    debouncedFilters,
    isFiltersDialogOpen,
    openFiltersDialog,
    closeFiltersDialog,
    handleFiltersUpdated,
    clearFilters,
  } = useDialogState()
  const savedViews = useSavedFilterTabsState(
    SAVED_FILTER_PAGES.uploadedLeads,
    debouncedFilters,
    setFilters,
    setPageIndex,
    {
      filters,
      normalizeFilters: normalizeLeadCallActivityFilters,
      sortColumn,
      sortDirection,
      setSort,
      defaultSortColumn: "total_production",
      defaultSortDirection: "desc",
    }
  )
  const { savedFiltersReady } = savedViews
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false)
  const [history, setHistory] = useState({
    upload_count: 0,
    success_count: 0,
    error_count: 0,
    skipped_count: 0,
    results: [],
  })
  const [isHistoryLoading, setIsHistoryLoading] = useState(false)
  const [addToCandidatesDialogOpen, setAddToCandidatesDialogOpen] =
    useState(false)
  const [deleteLeadsDialogOpen, setDeleteLeadsDialogOpen] = useState(false)
  const {
    isOpen: isSnackbarOpen,
    open: openSnackbar,
    close: closeSnackbar,
    message: snackbarMessage,
  } = useCloseableSnackbarState()
  const leadContactActions = useLeadContactActions(openSnackbar, {
    leadSource: "uploaded",
  })
  const profile = useSelector(selectProfile)
  const profileLoaded = useSelector(selectProfileLoaded)
  const subscription = useSelector(selectSubscription)
  const dispatch = useDispatch()
  const hasPaidPlan = isActiveSubscriptionStatus(subscription?.status)
  const showPhoneColumn = isConcierge(profile)
  const tableColumnCount = LEAD_LIST_TABLE_COLUMN_COUNT + 1
  const [actionMenuAnchorElem, setActionMenuAnchorElem] = useState(null)
  const [dncDialogOpen, setDncDialogOpen] = useState(false)
  const leadFilterConfig = useMemo(
    () =>
      mergeLeadFilterConfig(
        BASE_FILTER_FIELDS,
        BASE_FIELD_NAME_TO_COMPONENT_MAP,
        profile
      ),
    [profile]
  )
  const axios = useAuthorizedAxiosClient()
  const handleLeadRatingChanged = createLeadRatingChangeHandler({
    axios,
    leadSource: "uploaded",
    setLeads,
    openSnackbar,
  })
  const prevFiltersKeyRef = useRef(JSON.stringify(debouncedFilters))

  const refreshLeads = useCallback(() => {
    if (!profileLoaded || !savedFiltersReady) {
      return
    }
    setIsLoading(true)
    axios
      .post(
        "/crm/api/uploaded-leads/",
        { filters: serializeUploadedLeadListFilters(debouncedFilters) },
        {
          params: getTableSearchParams(
            null,
            pageIndex,
            pageSize,
            leadListOrdering(serverSort)
          ),
        }
      )
      .then(({ data }) => {
        setLeads(data.results)
        setTotalResultsSize(data.count)
      })
      .finally(() => setIsLoading(false))
  }, [
    axios,
    debouncedFilters,
    pageIndex,
    pageSize,
    profileLoaded,
    savedFiltersReady,
    serverSort,
    setTotalResultsSize,
  ])

  useEffect(() => {
    refreshLeads()
  }, [refreshLeads])

  useEffect(() => {
    const filtersKey = JSON.stringify(debouncedFilters)
    if (prevFiltersKeyRef.current === filtersKey) {
      return
    }
    prevFiltersKeyRef.current = filtersKey
    setPageIndex(1)
  }, [debouncedFilters, setPageIndex])

  useEffect(() => {
    const q = (searchParams.get("q") || "").trim()
    if (!q) return
    setFilters((current) => {
      if (
        current.some(
          (filter) =>
            filter.field === REALITY_FIELD_NAMES.NAME &&
            [].concat(filter.value ?? []).join(", ") === q
        )
      ) {
        return current
      }
      return [
        ...current,
        {
          field: REALITY_FIELD_NAMES.NAME,
          type: "contains",
          value: [q],
        },
      ]
    })
  }, [searchParams, setFilters])

  const loadHistory = useCallback(() => {
    setIsHistoryLoading(true)
    return axios
      .get("/crm/api/uploaded-leads/import-jobs/")
      .then(({ data }) => setHistory(data))
      .finally(() => setIsHistoryLoading(false))
  }, [axios])

  const handleImportJobComplete = useCallback(
    (job) => {
      openSnackbar(job.message || "Uploaded lead import finished")
      refreshLeads()
      loadHistory()
      dispatch(retrieveNotifications())
    },
    [dispatch, openSnackbar, refreshLeads, loadHistory]
  )

  const { startPolling } = useUploadedLeadImportJobPolling(
    handleImportJobComplete
  )

  function handleSelectAllCheckboxChanged(e) {
    // DNC leads are never checked: the Power Dialer won't call them.
    setCheckedIDS(toggleSelectableRows(checkedIDS, leads, e.target.checked))
  }

  function handleViewLeadDetails(lead) {
    const rowIndex = leads.findIndex(
      (row) => String(row.id) === String(lead.id)
    )
    navigateToLeadDetails(
      navigate,
      "uploaded",
      lead.id,
      buildLeadsListContext({
        leadSource: "uploaded",
        filters: serializeUploadedLeadListFilters(debouncedFilters),
        ordering: leadListOrdering(serverSort),
        pageIndex,
        pageSize,
        rowIndex: rowIndex < 0 ? 0 : rowIndex,
        pageLeadIds: leads.map((row) => row.id),
        totalCount: totalResultsSize,
      })
    )
  }

  function handleImportQueued(data) {
    openSnackbar("Uploaded lead import started...")
    startPolling(data.job_id)
  }

  function handleAddToCandidatesDialogClosed(importFinished) {
    if (importFinished) {
      openSnackbar("Importing leads in progress")
      setLeads(
        leads.map((lead) =>
          checkedIDS.has(lead.id) ? { ...lead, imported: true } : lead
        )
      )
      clearSelection()
    }
    setAddToCandidatesDialogOpen(false)
  }

  function handleDeleteLeadsDialogClosed(confirmed, deletedCount, deleteScope) {
    setDeleteLeadsDialogOpen(false)
    if (confirmed === "error") {
      openSnackbar("Could not delete import leads. Try again.")
      return
    }
    if (!confirmed) {
      return
    }

    if (deleteScope === "all") {
      clearSelection()
      refreshLeads()
    } else {
      const selectedLeadIdsSet = new Set(checkedIDsArray)
      setLeads((prev) =>
        prev.filter((lead) => !selectedLeadIdsSet.has(lead.id))
      )
      setTotalResultsSize((count) => Math.max(0, count - deletedCount))
      clearSelection()
    }
    openSnackbar(
      `${deletedCount.toLocaleString()} import lead${
        deletedCount === 1 ? "" : "s"
      } deleted`
    )
  }

  useEffect(() => {
    if (!onHeaderActionsChange) {
      return undefined
    }

    onHeaderActionsChange(
      <RowButColumnOnMobile
        sx={{
          alignItems: { xs: "flex-end", sm: "center" },
          rowGap: 1,
          columnGap: 2,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircleOutlineOutlinedIcon />}
          onClick={() => setAddToCandidatesDialogOpen(true)}
          disabled={checkedIDS.size === 0}
        >
          Add to Candidates
        </Button>
        <ApplyFiltersButton
          filters={filters}
          openFiltersDialog={openFiltersDialog}
        />
        {hasPaidPlan && canUsePowerDialer(profile) ? (
          <>
            <StartPowerDialerButton
              source={POWER_DIALER_SOURCES.uploaded}
              filters={serializeUploadedLeadListFilters(debouncedFilters)}
              ordering={serverSort}
              selectedIds={checkedIDsArray}
            />
            <AssignToPodButton
              source={POWER_DIALER_SOURCES.uploaded}
              filters={serializeUploadedLeadListFilters(debouncedFilters)}
              ordering={serverSort}
              selectedIds={checkedIDsArray}
            />
          </>
        ) : null}
        <IconButton
          onClick={(e) => setActionMenuAnchorElem(e.currentTarget)}
          aria-label="Options"
        >
          <MoreVertOutlinedIcon />
        </IconButton>
        <Menu
          open={Boolean(actionMenuAnchorElem)}
          anchorEl={actionMenuAnchorElem}
          onClose={() => setActionMenuAnchorElem(null)}
        >
          <MenuItem
            onClick={() => {
              setActionMenuAnchorElem(null)
              setDeleteLeadsDialogOpen(true)
            }}
            disabled={checkedIDS.size === 0}
          >
            <ListItemIcon>
              <DeleteOutlinedIcon />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setActionMenuAnchorElem(null)
              setImportDialogOpen(true)
            }}
          >
            <ListItemIcon>
              <CloudUploadOutlinedIcon />
            </ListItemIcon>
            <ListItemText>Import Leads</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setActionMenuAnchorElem(null)
              setDncDialogOpen(true)
            }}
          >
            <ListItemIcon>
              <CloudUploadOutlinedIcon color="error" />
            </ListItemIcon>
            <ListItemText>Upload DNC List</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setActionMenuAnchorElem(null)
              setHistoryDrawerOpen(true)
              loadHistory()
            }}
          >
            <ListItemIcon>
              <HistoryOutlinedIcon />
            </ListItemIcon>
            <ListItemText>History</ListItemText>
          </MenuItem>
        </Menu>
        <FiltersDialog
          open={isFiltersDialogOpen}
          onClosed={closeFiltersDialog}
          parentFilters={filters}
          onFiltersUpdated={handleFiltersUpdated}
          onFiltersCleared={clearFilters}
          filterTypes={leadFilterConfig.filterFields.filter(
            (field) =>
              showPhoneColumn || field.fieldName !== REALITY_FIELD_NAMES.PHONE
          )}
          fieldNameToComponentMap={leadFilterConfig.bodyMap}
        />
      </RowButColumnOnMobile>
    )

    return () => onHeaderActionsChange(null)
  }, [
    checkedIDS.size,
    onHeaderActionsChange,
    filters,
    isFiltersDialogOpen,
    openFiltersDialog,
    closeFiltersDialog,
    handleFiltersUpdated,
    clearFilters,
    leadFilterConfig,
    showPhoneColumn,
    loadHistory,
    hasPaidPlan,
    debouncedFilters,
    serverSort,
    actionMenuAnchorElem,
    checkedIDsArray,
    profile,
  ])

  return (
    <Column>
      <SectionSeparator sx={{ mt: 0 }}>
        <SavedFilterTabs {...savedViews} />
        <ConditionalPurpleLinearProgress visible={isLoading} />
        <ScrollableTableContainer
          sx={{
            maxHeight: {
              xs: "calc(100vh - 220px)",
              sm: "calc(100vh - 430px)",
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <StickyHeaderTableCell sx={stickyCheckboxCellSx}>
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={
                      !isLoading && allSelectableChecked(checkedIDS, leads)
                    }
                    onChange={handleSelectAllCheckboxChanged}
                  />
                </StickyHeaderTableCell>
                <LeadListTableHeaderCells
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSortColumnChanged={onSortColumnChanged}
                  stickyNameCellSx={stickyNameCellSx}
                  includeUploadName
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 10 }).map((_, index) => (
                  <TableRow key={index}>
                    <StickyTableCell sx={stickyCheckboxCellSx}>
                      <Skeleton variant="rectangular" width={24} height={24} />
                    </StickyTableCell>
                    <StickyTableCell sx={stickyNameCellSx}>
                      <Skeleton variant="text" width={180} />
                    </StickyTableCell>
                    <LeadListTableLoadingCells includeUploadName />
                  </TableRow>
                ))
              ) : leads.length === 0 ? (
                <NoResultsTableRow colSpan={tableColumnCount}>
                  No imported leads yet. Use Import Leads to upload a CSV file.
                </NoResultsTableRow>
              ) : (
                leads.map((lead, index) => (
                  <TableRow
                    key={lead.id}
                    onClick={(e) => {
                      if (shouldIgnoreLeadRowClick(e)) {
                        return
                      }
                      handleViewLeadDetails(lead)
                    }}
                    sx={{
                      opacity: lead.imported ? 0.5 : 1,
                      cursor: "pointer",
                      ...dncRowSx(lead),
                    }}
                  >
                    <SelectMultipleTableCell
                      checkedIDs={checkedIDS}
                      setCheckedIDS={setCheckedIDS}
                      row={lead}
                      rows={leads}
                      index={index}
                      disabled={lead.imported || isDncLead(lead)}
                      disabledTooltip={
                        isDncLead(lead) ? DNC_SELECT_TOOLTIP : undefined
                      }
                      CellComponent={StickyTableCell}
                      cellProps={{ sx: stickyCheckboxCellSx }}
                    />
                    <NameWithContactActionsCell
                      name={lead.name}
                      title={lead.name}
                      phone={leadPhoneValue(lead)}
                      email={lead.email}
                      isDnc={Boolean(lead.is_dnc)}
                      showActions={hasPaidPlan}
                      onCall={(e) =>
                        leadContactActions.handleCallButtonClicked(e, lead)
                      }
                      onSMS={(e) =>
                        leadContactActions.handleSMSButtonClicked(e, lead)
                      }
                      onEmail={(e) =>
                        leadContactActions.handleEmailButtonClicked(e, lead)
                      }
                    />
                    <LeadListTableDataCells
                      lead={lead}
                      includeUploadName
                      onRatingChanged={handleLeadRatingChanged}
                    />
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollableTableContainer>
        <TablePaginationRow
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
          pageSize={pageSize}
          setPageSize={setPageSize}
          tableRows={leads}
          totalResultsSize={totalResultsSize}
        />
      </SectionSeparator>

      <ImportHistoryDrawer
        open={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
        history={history}
        isLoading={isHistoryLoading}
        onDownloadError={openSnackbar}
      />
      <ImportUploadedLeadsDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImportQueued={handleImportQueued}
      />
      <ConfirmAddLeadsToCandidatesDialog
        open={addToCandidatesDialogOpen}
        onClose={handleAddToCandidatesDialogClosed}
        selectedLeadIDS={checkedIDsArray}
        apiEndpoint="/crm/api/uploaded-leads/add-to-candidates/"
        requestBodyKey="uploaded_lead_ids"
        trackingEventName="Uploaded leads imported"
      />
      <ConfirmDeleteUploadedLeadsDialog
        open={deleteLeadsDialogOpen}
        onClose={handleDeleteLeadsDialogClosed}
        selectedCount={checkedIDS.size}
        totalCount={totalResultsSize}
        selectedLeadIds={checkedIDsArray}
        filters={serializeUploadedLeadListFilters(filters)}
      />
      <UploadDncDialog
        open={dncDialogOpen}
        onClose={() => setDncDialogOpen(false)}
        onSuccess={(data) => {
          setDncDialogOpen(false)
          openSnackbar(data?.message || "DNC list uploaded successfully.")
          refreshLeads()
        }}
      />
      <LeadContactActionDialogs
        actionsState={leadContactActions}
        openSnackbar={openSnackbar}
      />
      <CloseableSnackbar
        open={isSnackbarOpen}
        message={snackbarMessage}
        onClose={closeSnackbar}
      />
    </Column>
  )
}

const BASE_FILTER_FIELDS = filterFieldsFor(SAVED_FILTER_PAGES.uploadedLeads, [
  REALITY_FIELD_NAMES.NAME,
  REALITY_FIELD_NAMES.MLS,
  REALITY_FIELD_NAMES.ANNUAL_VALUE,
  REALITY_FIELD_NAMES.POTENTIAL_TIER,
  REALITY_FIELD_NAMES.ROLE,
  REALITY_FIELD_NAMES.EMAIL,
  REALITY_FIELD_NAMES.PHONE,
  REALITY_FIELD_NAMES.HAS_CALLABLE_PHONE,
  REALITY_FIELD_NAMES.CITY,
  REALITY_FIELD_NAMES.STATE,
  REALITY_FIELD_NAMES.ZIPCODE,
  REALITY_FIELD_NAMES.SALES_VOLUME,
  REALITY_FIELD_NAMES.NO_OF_SALES,
  REALITY_FIELD_NAMES.YEARS_IN_BUSINESS,
  REALITY_FIELD_NAMES.OFFICE_NAME,
  REALITY_FIELD_NAMES.UPLOAD_NAME,
  REALITY_FIELD_NAMES.IS_DNC,
  REALITY_FIELD_NAMES.CUSTOM_TAG,
])

const BASE_FIELD_NAME_TO_COMPONENT_MAP = {
  ...Object.fromEntries(
    BASE_FILTER_FIELDS.map((field) => [field.fieldName, SchemaBody])
  ),
  [REALITY_FIELD_NAMES.POTENTIAL_TIER]: PotentialFilterBody,
  [REALITY_FIELD_NAMES.ROLE]: RoleFilterBody,
}
